// ─── Enums (matching Prisma enums as string unions) ──────────────────────────

import { z } from 'zod'
import type { PayrollUrenCorrectieDTO } from './payroll-uren-correctie'

export const PAYROLL_EXPORT_STATUS = [
  'CONCEPT',
  'GEGENEREERD',
  'GEDOWNLOAD',
  'VERWERKT',
  'INGETROKKEN',
  // FO-303-P3 — bij de bevestig-preview afgewezen (reden + audit; run vervalt).
  'AFGEWEZEN',
] as const
export type PayrollExportStatus = (typeof PAYROLL_EXPORT_STATUS)[number]

export const PAYROLL_EXPORT_AUDIT_ACTIE = [
  'AANGEMAAKT',
  'GEGENEREERD',
  'GEDOWNLOAD',
  'VERWERKT',
  'INGETROKKEN',
  'REGEL_AANGEPAST',
  'AFGEWEZEN',
] as const
export type PayrollExportAuditActie = (typeof PAYROLL_EXPORT_AUDIT_ACTIE)[number]

// ─── Label constants (NL-talig voor UI) ──────────────────────────────────────

export const PAYROLL_EXPORT_STATUS_LABELS: Record<PayrollExportStatus, string> = {
  CONCEPT:     'Concept',
  GEGENEREERD: 'Gegenereerd',
  GEDOWNLOAD:  'Gedownload',
  VERWERKT:    'Verwerkt',
  INGETROKKEN: 'Ingetrokken',
  AFGEWEZEN:   'Afgewezen',
}

export const PAYROLL_EXPORT_AUDIT_ACTIE_LABELS: Record<PayrollExportAuditActie, string> = {
  AANGEMAAKT:      'Aangemaakt',
  GEGENEREERD:     'Gegenereerd',
  GEDOWNLOAD:      'Gedownload',
  VERWERKT:        'Verwerkt',
  INGETROKKEN:     'Ingetrokken',
  REGEL_AANGEPAST: 'Regel aangepast',
  AFGEWEZEN:       'Afgewezen',
}

export const PAYROLL_EXPORT_STATUS_BADGE_VARIANT: Record<
  PayrollExportStatus,
  'info' | 'warning' | 'success' | 'danger' | 'secondary'
> = {
  CONCEPT:     'secondary',
  GEGENEREERD: 'info',
  GEDOWNLOAD:  'warning',
  VERWERKT:    'success',
  INGETROKKEN: 'danger',
  AFGEWEZEN:   'danger',
}

// ─── DTOs voor API responses ─────────────────────────────────────────────────

export interface PayrollExportListRowDTO {
  id: string
  year: number
  month: number
  status: PayrollExportStatus
  totaalBruto: number | null
  totaalRegels: number
  generatedAt: string | null            // ISO datetime
  generatedByName: string | null
  csvFilePath: string | null            // null als nog niet gegenereerd
  createdAt: string                     // ISO datetime
  verwerktOp: string | null             // ISO datetime
}

/**
 * KPI-aggregaten voor de payroll-lijst-pagina. Wordt door de API meegestuurd
 * in `meta.kpi` zodat de lijst-pagina geen aparte stats-call hoeft te doen.
 */
export interface PayrollListKpiDTO {
  conceptCount: number
  gegenereerdCount: number
  verwerktThisYearCount: number
  brutoTotalYtd: number
}

export interface PayrollExportRegelDTO {
  id: string
  payrollExportId: string
  employeeId: string
  employeeNumber: string
  naam: string
  contractUren: number
  brutoMaandSalaris: number | null
  brutoUren: number
  overuren: number
  ploegtoeslagBedrag: number
  reisKm: number
  /**
   * FO-411 (batch-54) — INFORMATIEF: "Km doorbelast (facturatie)". De
   * kilometervergoeding (km x km-tarief) die via de werkbon naar de
   * klantfactuur gaat. Telt NIET in `brutoTotaal` en is dus geen looncomponent
   * meer; hij blijft zichtbaar zodat het verband uren <-> kilometers
   * controleerbaar blijft. De uitzondering staat in `kmVergoedingNetto`.
   */
  reisVergoeding: number
  /**
   * FO-411 (batch-54) — het deel van `reisVergoeding` dat WEL wordt uitbetaald,
   * omdat de medewerker de regeling "eigen vervoer - km-vergoeding uitbetalen"
   * heeft (`Employee.eigenVervoerKmVergoeding`, per leg-datum vanaf de
   * ingangsdatum). NETTO-onkostencomponent met een eigen NMBRS-looncode: telt
   * NIET in `brutoTotaal`. 0 zonder de regeling (het normale geval).
   */
  kmVergoedingNetto: number
  /**
   * Looncomponent REISTIJD: aantal uitbetaalde uren reistijd (heen+terug,
   * werkelijk bij goedgekeurde afwijking, anders berekend). Sinds migratie
   * `payroll-export-regel-reistijduren` gepersisteerd in `payroll_export_regels`
   * en zichtbaar in detail-views. Bedrag (uren × uurtarief) zit verwerkt in
   * `brutoTotaal`. Wel zichtbaar in de gegenereerde CSV.
   */
  reistijdUren: number
  declaratiesTotaal: number
  verlofUitbetaling: number
  /**
   * Verjaardag-als-feestdag toeslag (spec §2.3). `verjaardagToeslagUren` is
   * de som van gewerkte uren op een verjaardagdatum met keuze=BETAALD;
   * `verjaardagToeslagBedrag` is uren × uurloon (de extra 100% bovenop het
   * normale loon dat al in `brutoUren` zit).
   */
  verjaardagToeslagUren: number
  verjaardagToeslagBedrag: number
  /**
   * Spec 23 fase 2 — losse dagtype-toeslagen (zitten óók in `brutoTotaal`).
   * Gepersisteerd zodat de detail-view de breakdown toont en de som van de
   * zichtbare componenten aansluit op `brutoTotaal` (geen onverklaard gat).
   */
  zaterdagToeslagBedrag: number
  zondagToeslagBedrag: number
  feestdagToeslagBedrag: number
  overigeToeslagBedrag: number
  /**
   * Batch-9-vervolg V4 (PO #2) — verlof + ziekte als aparte componenten.
   * `verlofUren`/`ziekUren` zijn OPGENOMEN uren in de periode (betaald maar geen
   * gewone werkuren); ze zitten NIET in `brutoUren` en NIET in `brutoTotaal`
   * (geen dubbeltelling). `ziekUitbetaling` (de loondoorbetaling) zit wél in
   * `brutoTotaal`.
   */
  verlofUren: number
  ziekUren: number
  ziekUitbetaling: number
  /**
   * FO-389 (batch-46) — consignatie/standby. `consignatieWeken` = getelde
   * piketweken uit `ConsignatieRooster` (donderdag-allocatie, dedupe per
   * (employee, ISO-week)); informatief aantal, telt NIET in `brutoTotaal`.
   * `consignatieVergoeding` = weken x verloningstarief, telt WEL in
   * `brutoTotaal`. `consignatieOproepUren` = INFORMATIEF label op oproep-arbeid
   * die al in `brutoUren`/`brutoTotaal` zit (telt dus niet nogmaals; patroon
   * `verlofUren`). 0 zolang de component niet geactiveerd is (§3.5).
   */
  consignatieWeken: number
  consignatieVergoeding: number
  consignatieOproepUren: number
  brutoTotaal: number
  /**
   * Ronde 4 W8 (spec 08 §9.1) — capaciteits-snapshot over de payroll-periode,
   * gepersisteerd bij het genereren van de export. `null` voor oude regels
   * (vóór de capaciteits-velden) of medewerkers zonder contracturen.
   */
  beschikbareUren: number | null
  geplandeUren: number | null
  bezettingsgraad: number | null
  notitie: string | null
  createdAt: string
  updatedAt: string
}

export interface PayrollExportAuditDTO {
  id: string
  actie: PayrollExportAuditActie
  actor: string
  actorName: string | null
  oudeWaarde: unknown | null
  nieuweWaarde: unknown | null
  toelichting: string | null
  createdAt: string
}

export interface PayrollExportDetailDTO {
  id: string
  year: number
  month: number
  status: PayrollExportStatus
  csvFilePath: string | null
  totaalBruto: number | null
  totaalRegels: number
  generatedBy: string | null
  generatedByName: string | null
  generatedAt: string | null
  downloadedBy: string | null
  downloadedByName: string | null
  downloadedAt: string | null
  verwerktDoor: string | null
  verwerktDoorName: string | null
  verwerktOp: string | null
  createdAt: string
  updatedAt: string
  regels: PayrollExportRegelDTO[]
  audits: PayrollExportAuditDTO[]
}

/**
 * FO-325 — herleidings-component voor het opbouw-paneel: één post die bijdraagt
 * aan het bruto-totaal, met de rekenkundige herleiding (uren x tarief x % =
 * bedrag) en de bron. `percentage`/`tarief`/`uren` zijn null/0 waar niet van
 * toepassing (bv. declaraties = een vast bedrag). De UI rekent NIETS - ze toont
 * deze reeds-berekende waarden.
 */
export type PayrollComponentSoort =
  | 'BASIS'
  | 'OVERUREN'
  | 'PLOEGTOESLAG'
  | 'ZATERDAG_TOESLAG'
  | 'ZONDAG_TOESLAG'
  | 'FEESTDAG_TOESLAG'
  | 'OVERIG_TOESLAG'
  | 'VERJAARDAG_TOESLAG'
  | 'REISTIJD'
  // FO-411 (batch-54) — de km-vergoeding is geen BRUTO-component meer (de oude
  // soort 'REISVERGOEDING' is vervallen). Wat overblijft is de netto-variant:
  // hij verschijnt in `nettoComponenten`, nooit in de bruto-opbouw.
  | 'KM_VERGOEDING_NETTO'
  | 'DECLARATIES'
  | 'VERLOF_UITBETALING'
  | 'ZIEK_UITBETALING'
  // FO-389 (batch-46) — consignatie/standby-vergoeding (uren=piketweken, tarief=verloningstarief).
  | 'CONSIGNATIE'

export interface PayrollControleComponentDTO {
  soort: PayrollComponentSoort
  label: string
  /** Uren waarop de component rust (0 als n.v.t.). */
  uren: number
  /** Uurtarief (globaal of persoonlijk); 0 als n.v.t. */
  tarief: number
  /** Percentage als FRACTIE (0.26 = 26%) bij een toeslag; null als n.v.t. */
  percentage: number | null
  /** Resulterend bedrag (reeds berekend door de engine). */
  bedrag: number
  /** Bronvermelding: welke weekstaten/dagen, welk tarief, welke regelset. */
  bron: string
}

/**
 * Preview-output zonder DB-write. Bevat dezelfde regel-structuur als detail
 * maar dan zonder ids — caller kan deze direct in een tabel renderen om te
 * verifiëren vóór genereren.
 */
export interface PayrollExportPreviewRegelDTO {
  employeeId: string
  employeeNumber: string
  naam: string
  contractUren: number
  brutoMaandSalaris: number | null
  brutoUren: number
  overuren: number
  ploegtoeslagBedrag: number
  /** Spec 23 fase 2 — losse dagtype-toeslagen (zitten óók in brutoTotaal; zonder
   * deze regels sluit de preview-brutoTotaal niet aan op de zichtbare kolommen). */
  zaterdagToeslagBedrag: number
  zondagToeslagBedrag: number
  feestdagToeslagBedrag: number
  overigeToeslagBedrag: number
  reisKm: number
  /**
   * FO-411 (batch-54) — INFORMATIEVE facturatie-kolom "Km doorbelast
   * (facturatie)", BUITEN het bruto-blok. Telt nergens in op (niet in
   * `brutoTotaal`, niet in de bruto-`componenten`, niet in de som-guard).
   */
  reisVergoeding: number
  /**
   * FO-411 (batch-54) — netto uitbetaalde km-vergoeding (alleen bij de
   * eigen-vervoer-regeling, per leg-datum). Eigen netto-component + looncode;
   * telt NIET in `brutoTotaal` maar wél in `nettoTotaal`.
   */
  kmVergoedingNetto: number
  /** Looncomponent REISTIJD — uitbetaalde uren (zie PayrollExportRegelDTO). */
  reistijdUren: number
  declaratiesTotaal: number
  verlofUitbetaling: number
  /** Verjaardag-als-feestdag — zelfde semantiek als PayrollExportRegelDTO. */
  verjaardagToeslagUren: number
  verjaardagToeslagBedrag: number
  /** FO-305 §7 — uren-uitsplitsing in de bevestig-preview (zelfde bron als het
   *  verloningsperiode-overzicht): overuren-staffel 100/125/150/200, ploegtoeslag-
   *  uren per soort (2/3/5-ploegen + za/zo/feestdag/overig) en uit te betalen reisuren. */
  urenPerOverwerkTrede: OverurenTredenDTO
  toeslagUren: ToeslagUrenDTO
  reisUrenUitbetaald: number
  // FO-325 — ALLE euro-componenten die in `brutoTotaal` zitten, expliciet zichtbaar
  // zodat de rij herleidbaar optelt (geen onzichtbare component in het controle-
  // scherm). basisBedrag = werkuren x uurtarief; reistijdBedrag = uitbetaalde
  // reisuren x tarief; toeslagenBedragTotaal = som van alle toeslag-bedragen.
  basisBedrag: number
  overurenBedrag: number
  reistijdBedrag: number
  ziekUitbetaling: number
  /**
   * FO-389 (batch-46) — consignatie/standby (zie `PayrollExportRegelDTO`).
   * `consignatieVergoeding` zit in `brutoTotaal`; `consignatieOproepUren` is
   * informatief (al in `brutoUren`). 0 zolang de component niet geactiveerd is.
   */
  consignatieWeken: number
  consignatieVergoeding: number
  consignatieOproepUren: number
  toeslagenBedragTotaal: number
  /**
   * Herleidings-opbouw per BRUTO-component (uren x tarief x % = bedrag) voor het
   * paneel. FO-411: de km-vergoeding staat hier NIET meer in - die is geen
   * looncomponent; de netto-uitzondering staat in `nettoComponenten`.
   */
  componenten: PayrollControleComponentDTO[]
  /**
   * FO-411 (batch-54) — de NETTO-onkostencomponenten (nu alleen de
   * km-vergoeding bij eigen vervoer). Leeg voor iedereen zonder de regeling.
   * Staat bewust los van `componenten`: netto is geen loon, en de guard
   * reconcilieert bruto en netto apart.
   */
  nettoComponenten: PayrollControleComponentDTO[]
  /** FO-411 — som van de netto-componenten (`berekenNettoVergoedingTotaal`). */
  nettoTotaal: number
  /**
   * FO-325 reconciliatie: brutoTotaal - som(componenten). ~0 na de fix; een
   * niet-nul afwijking is een SIGNAAL (rij-som-guard blokkeert de export).
   */
  afwijking: number
  /**
   * FO-411 reconciliatie op de netto-kant: nettoTotaal - som(nettoComponenten).
   * Zelfde guard-mechaniek als `afwijking`, apart gehouden zodat een netto-drift
   * nooit als bruto-drift wordt gelezen (en omgekeerd).
   */
  nettoAfwijking: number
  /** Plausibiliteit: bruto boven de instelbare drempel -> waarschuwing-badge. */
  boveDrempel: boolean
  /**
   * FO-363 — de persoonlijke uurwaarde (EUR/uur) op de periode-peildatum waarmee
   * de uur-componenten (overuren/reistijd/toeslagen/oproep-basis) zijn berekend;
   * null wanneer salaris of contracturen ontbreken (dan zijn die bedragen EUR 0).
   */
  uurwaarde: number | null
  /** FO-363 — bron van de uurwaarde: "salarismutatie per dd-mm-jjjj" (drilldown). */
  uurwaardeBron: string | null
  /**
   * FO-363 — hoe het basisloon is opgebouwd: MAANDLOON (vast contract, pro-rata),
   * OPROEP_UREN (uren x persoonlijke uurwaarde) of MENG (contract(type)wissel
   * binnen de maand: deels maandloon-dagen, deels oproep-uren).
   */
  basisSoort: 'MAANDLOON' | 'OPROEP_UREN' | 'MENG'
  /**
   * FO-363 — loon ontbreekt: een component had de persoonlijke loonwaarde nodig
   * maar die is er niet (geen salarismutatie / geen contracturen op peildatum).
   * Het bedrag is dan EUR 0 (nooit een stil fallback-tarief) en de NMBRS-export
   * blokkeert voor deze medewerker tot HR een salarismutatie invoert.
   */
  loonOntbreekt: boolean
  brutoTotaal: number
}

/**
 * FO-325 — server-berekende totalenrij (de UI rekent niets). Per zichtbare kolom
 * de som over alle regels; `brutoTotaal` == `PayrollExportPreviewDTO.totaalBruto`.
 */
export interface PayrollExportPreviewTotalenDTO {
  brutoUren: number
  overuren: number
  zaterdagUren: number
  zondagUren: number
  reisUrenUitbetaald: number
  basisBedrag: number
  overurenBedrag: number
  toeslagenBedragTotaal: number
  reistijdBedrag: number
  /**
   * FO-411 (batch-54) — som van de INFORMATIEVE facturatie-km over alle regels.
   * Staat buiten het bruto-blok en telt NIET mee in `brutoTotaal`.
   */
  reisVergoeding: number
  /** FO-411 (batch-54) — som van de netto uitbetaalde km-vergoeding (eigen vervoer). */
  kmVergoedingNetto: number
  declaratiesTotaal: number
  verlofUitbetaling: number
  ziekUitbetaling: number
  /** FO-389 (batch-46) — som van de consignatievergoeding over alle regels. */
  consignatieVergoeding: number
  brutoTotaal: number
}

/**
 * FO-389 (batch-46) §3.3/§3.6 — consignatie-reconciliatie in de export-preview.
 * Per medewerker de getelde piketweken (weeknummer + klant), doorklikbaar naar
 * het rooster, met het dubbele-bezetting-signaal. Handmatig weken toevoegen
 * bestaat niet; klopt het rooster niet, dan wordt het rooster gecorrigeerd.
 */
export interface PayrollConsignatieWeekDTO {
  weekNr: string
  clientIds: string[]
  clientNamen: string[]
  /** Monteur stond die week voor > 1 klant ingepland → één vergoeding + signaal. */
  dubbeleBezetting: boolean
}

export interface PayrollConsignatieMedewerkerDTO {
  employeeId: string
  employeeNumber: string
  naam: string
  weken: number
  /** Vergoeding zoals die telt (0 zolang de component niet actief of tarief 0 is). */
  vergoeding: number
  weekDetails: PayrollConsignatieWeekDTO[]
}

export const CONSIGNATIE_EENHEID = ['WEEK', 'NACHT'] as const
export type ConsignatieEenheid = (typeof CONSIGNATIE_EENHEID)[number]

export interface PayrollConsignatiePreviewDTO {
  /** Component geactiveerd door SALARIS (echte U-code bevestigd)? */
  actief: boolean
  eenheid: ConsignatieEenheid
  /** Het gelezen verloningstarief (EUR per week/nacht); 0 = niet ingesteld. */
  tarief: number
  /** Er zijn piketweken maar de component is nog niet geactiveerd (wacht-melding). */
  wachtOpActivatie: boolean
  /** Component actief én er zijn piketweken, maar het tarief ontbreekt → blokkade bij genereren. */
  tariefOntbreekt: boolean
  /** Minstens één medewerker had dubbele bezetting deze maand (preview-signaal). */
  heeftDubbeleBezetting: boolean
  medewerkers: PayrollConsignatieMedewerkerDTO[]
}

export interface PayrollExportPreviewDTO {
  year: number
  month: number
  totaalBruto: number
  totaalRegels: number
  regels: PayrollExportPreviewRegelDTO[]
  /** FO-325 — server-berekende totalenrij (per kolom). */
  totalen: PayrollExportPreviewTotalenDTO
  /**
   * FO-305 §7 — de uren-correcties (P3) die op deze maand zijn toegepast, als
   * regels zodat de beoordelaar ziet wat er is geherclassificeerd (verrekend ↔
   * uitbetalen, ploeg ↔ regulier, overwerk-trede) vóór bevestigen. De effecten
   * zitten al verwerkt in de regel-cijfers hierboven (aggregatie past ze toe).
   */
  correcties: PayrollUrenCorrectieDTO[]
  /**
   * FO-303-P3 — hash over exact deze preview-inhoud. De gebruiker stuurt 'm mee bij
   * bevestigen; de server herberekent 'm uit dezelfde bron en weigert (409) als de
   * bron intussen wijzigde, zodat bevestigd wordt wat de preview toonde.
   */
  previewToken: string
  /** FO-325 — plausibiliteitsdrempel (EUR); rij met bruto boven dit bedrag = badge. */
  plausibiliteitsDrempel: number
  /**
   * FO-325 reconciliatie op run-niveau: `kopAfwijking` = totaalBruto - som van de
   * rij-bruto's (~0). `heeftAfwijking` = een rij sluit niet (bruto != som
   * componenten) OF de kop sluit niet -> de export blokkeert (rij-som/kop-som-guard).
   */
  kopAfwijking: number
  heeftAfwijking: boolean
  /**
   * FO-389 (batch-46) — consignatie-reconciliatie: getelde piketweken per
   * medewerker (weeknummer + klant + dubbele-bezetting-signaal) + de activatie-/
   * tarief-status. Read-only: de teller leest het rooster, handmatig toevoegen
   * bestaat niet.
   */
  consignatie: PayrollConsignatiePreviewDTO
}

// ─── Zod-schemas voor request bodies ─────────────────────────────────────────

const yearSchema = z.coerce.number().int().min(2000).max(2100)
const monthSchema = z.coerce.number().int().min(1).max(12)

export const createPayrollExportSchema = z.object({
  year:  yearSchema,
  month: monthSchema,
})

export const previewPayrollExportSchema = z.object({
  year:  yearSchema,
  month: monthSchema,
})

export const markGedownloadSchema = z.object({}).strict()

export const markVerwerktSchema = z.object({
  notitie: z.string().trim().max(500).optional(),
})

export const intrekkenSchema = z.object({
  reden: z.string().trim().min(1).max(500),
})

// FO-303-P3 — bevestigen (genereren) eist het preview-token; de server herberekent
// het en weigert bij drift (409). Optioneel zodat de detail-pagina een bestaand
// concept ongewijzigd kan hergenereren (die flow toont geen verse preview).
export const genereerPayrollExportSchema = z.object({
  previewToken: z.string().min(1).optional(),
})

// FO-303-P3 — afwijzen bij de bevestig-preview: verplichte reden (>= 5 tekens,
// zelfde conventie als de verlof-afwijzing). De run vervalt (soft-delete + audit).
export const afwijzenPayrollExportSchema = z.object({
  reden: z.string().trim().min(5, 'Reden moet minimaal 5 tekens bevatten').max(1000),
})

export const updateRegelSchema = z.object({
  notitie:            z.string().trim().max(500).nullable().optional(),
  brutoUren:          z.number().optional(),
  overuren:           z.number().optional(),
  ploegtoeslagBedrag: z.number().optional(),
  reisKm:             z.number().int().optional(),
  // FO-411 (batch-54) — `reisVergoeding` is hier BEWUST niet (meer) editbaar: het
  // is sinds de km-fix een informatieve spiegel van de facturatie (werkbon ->
  // factuurregel) en geen looncomponent. Een correctie erop zou het bruto niet
  // bewegen en dus stil niets doen; corrigeren hoort in de reisregistratie op de
  // werkbon. Wat wél corrigeerbaar is: de uitbetaalde netto km-vergoeding van de
  // regeling "eigen vervoer" - die telt in het netto-totaal, nooit in het bruto,
  // en mag de doorbelaste km niet overstijgen (server-side geklemd).
  kmVergoedingNetto:  z.number().optional(),
  declaratiesTotaal:  z.number().optional(),
  verlofUitbetaling:  z.number().optional(),
  // FO-389 (batch-46) — bedrag-correctie op de consignatievergoeding via de
  // bestaande regel-PATCH (REGEL_AANGEPAST, ADMIN-only). Delta 1:1 in brutoTotaal.
  consignatieVergoeding: z.number().optional(),
})

/**
 * FO-411 (batch-54) — query voor het km-verleden-rapport (read-only). `formaat`
 * default xlsx (download); `json` levert dezelfde regels als data.
 */
export const kmVerledenRapportQuerySchema = z.object({
  formaat:    z.enum(['xlsx', 'json']).default('xlsx'),
  vanafJaar:  yearSchema.optional(),
  totJaar:    yearSchema.optional(),
  employeeId: z.string().min(1).optional(),
})

export const listPayrollExportsQuerySchema = z.object({
  year:     yearSchema.optional(),
  // payroll-werkruimte 18→8 — maand-filter (lijst) + gerichte lookup voor de
  // verloningsperiode-kruislink (year+month is uniek op PayrollExport).
  month:    monthSchema.optional(),
  status:   z.enum(PAYROLL_EXPORT_STATUS).optional(),
  page:     z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(2000).default(20),
})

// ─── Ronde 4 W4 (spec 04 §1.2) — PayrollLock ────────────────────────────────

export const PAYROLL_PERIOD_TYPE = ['MONTH', 'FOUR_WEEK'] as const
export type PayrollPeriodType = (typeof PAYROLL_PERIOD_TYPE)[number]

export const PAYROLL_PERIOD_TYPE_LABELS: Record<PayrollPeriodType, string> = {
  MONTH:     'Maand',
  FOUR_WEEK: '4 weken',
}

export interface PayrollLockDTO {
  id: string
  periodStart: string   // ISO date yyyy-mm-dd
  periodEnd: string     // ISO date yyyy-mm-dd
  periodType: PayrollPeriodType
  lockedBy: string
  lockedByName: string | null
  lockedAt: string      // ISO timestamp
  reden: string | null
  payrollExportId: string | null
  // Aantal werkbonnen dat in deze periode valt (helpt UI om impact te tonen).
  werkbonnenInPeriode?: number
  // Soft-deleted? Wordt zo getoond in audit-overzicht (heropende periodes).
  isReleased: boolean
  releasedAt: string | null
}

export const isoDateOnly = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum moet yyyy-mm-dd zijn')

export const createPayrollLockSchema = z.object({
  periodStart:     isoDateOnly,
  periodEnd:       isoDateOnly,
  periodType:      z.enum(PAYROLL_PERIOD_TYPE).default('MONTH'),
  payrollExportId: z.string().min(1).optional(),
  reden:           z.string().trim().max(500).optional(),
})
export type CreatePayrollLockInput = z.infer<typeof createPayrollLockSchema>

// ─── Ronde 4 W4 (spec 04 §1.3) — PayrollCorrection ──────────────────────────

export const PAYROLL_CORRECTION_STATUS = [
  'OPEN',
  'GOEDGEKEURD',
  'AFGEWEZEN',
  'VERWERKT',
] as const
export type PayrollCorrectionStatus = (typeof PAYROLL_CORRECTION_STATUS)[number]

export const PAYROLL_CORRECTION_STATUS_LABELS: Record<PayrollCorrectionStatus, string> = {
  OPEN:        'Open - wacht op goedkeuring',
  GOEDGEKEURD: 'Goedgekeurd - wacht op volgende payroll',
  AFGEWEZEN:   'Afgewezen',
  VERWERKT:    'Verwerkt in payroll',
}

export const PAYROLL_CORRECTION_STATUS_BADGE: Record<
  PayrollCorrectionStatus,
  'secondary' | 'info' | 'warning' | 'success' | 'danger'
> = {
  OPEN:        'warning',
  GOEDGEKEURD: 'info',
  AFGEWEZEN:   'danger',
  VERWERKT:    'success',
}

export interface PayrollCorrectionDTO {
  id: string
  werkbonId: string
  werkbonNumber: string
  dayEntryId: string | null
  // Datum waarop de dayEntry valt (alleen aanwezig als dayEntryId niet null is).
  dayEntryDate: string | null

  employeeId: string
  employeeName: string

  origineleUren:     number
  origineleOveruren: number
  origineleReisKm:   number
  origineleReisMin:  number

  correctieUren:     number
  correctieOveruren: number
  correctieReisKm:   number
  correctieReisMin:  number

  // Delta's pre-computed voor de UI (correctie - origineel).
  deltaUren:     number
  deltaOveruren: number
  deltaReisKm:   number
  deltaReisMin:  number

  reden: string
  bewijsstukken: string[] | null

  aangevraagdDoor: string
  aangevraagdDoorNaam: string | null
  aangevraagdOp: string

  status: PayrollCorrectionStatus
  goedgekeurdDoor: string | null
  goedgekeurdDoorNaam: string | null
  goedgekeurdOp: string | null
  afwijzingReden: string | null

  verwerktePayrollExportId: string | null
}

export const createPayrollCorrectionSchema = z.object({
  werkbonId:         z.string().min(1),
  dayEntryId:        z.string().min(1).optional(),
  employeeId:        z.string().min(1),
  correctieUren:     z.number().min(0).max(24),
  correctieOveruren: z.number().min(0).max(24).default(0),
  correctieReisKm:   z.number().int().min(0).default(0),
  correctieReisMin:  z.number().int().min(0).max(24 * 60).default(0),
  reden:             z.string().trim().min(3).max(500),
  bewijsstukken:     z.array(z.string()).max(20).optional(),
})
export type CreatePayrollCorrectionInput = z.infer<typeof createPayrollCorrectionSchema>

export const afwijzenCorrectieSchema = z.object({
  reden: z.string().trim().min(3).max(500),
})
export type AfwijzenCorrectieInput = z.infer<typeof afwijzenCorrectieSchema>

// ─── Ronde 4 W4 (spec 04 §1.4 + §6) — WerkbonRevision ───────────────────────

export interface WerkbonRevisionDTO {
  id: string
  werkbonId: string
  revision: number
  action: string
  fromStatus: string | null
  toStatus: string | null
  createdBy: string
  createdByName: string | null
  createdAt: string
  // Snapshot wordt NIET in lijst-DTOs meegestuurd — pas bij detail-opvraag.
}

export interface WerkbonRevisionDetailDTO extends WerkbonRevisionDTO {
  snapshot: unknown // Json-object — UI parsest hierdoor
}

// ─── Ronde 4 W4 (spec 04 §9) — Trending / Monitoring ────────────────────────

export interface TrendingPerAanvragerRow {
  userId: string
  naam: string
  aantal: number
  totaalCorrUren: number
}

export interface TrendingPerMedewerkerRow {
  employeeId: string
  naam: string
  aantal: number
  totaalOrig: number
  totaalCorr: number
  deltaUren: number
}

export interface TrendingHeropeningRow {
  userId: string
  naam: string
  aantal: number
}

export interface PayrollTrendingDTO {
  correctiesPerAanvrager: TrendingPerAanvragerRow[]
  correctiesPerMedewerker: TrendingPerMedewerkerRow[]
  heropeningenPerUser: TrendingHeropeningRow[]
  // Hoeveel werkbonnen zijn er in deze periode VERWERKT_IN_VERLONING gegaan?
  werkbonnenVerwerkt: number
  // Hoeveel periodes zijn er in deze periode gesloten?
  periodesGesloten: number
}

export const trendingQuerySchema = z.object({
  startDate: isoDateOnly,
  endDate:   isoDateOnly,
})
export type TrendingQuery = z.infer<typeof trendingQuerySchema>

// ─── #51 — NMBRS export-variant (TOTAAL vs MUTATIES) ─────────────────────────
//
// De gegenereerde, bevroren PayrollExportRegel-rijen blijven de bron. De
// NMBRS-variant is een render-keuze op DOWNLOAD-moment (geen tweede aggregatie,
// geen schema-wijziging aan de hot PayrollExport-tabel). MUTATIES laat de
// contracturen weg (NMBRS pakt die zelf van de huidige maand); TOTAAL bevat ze.

export const PAYROLL_EXPORT_VARIANT = ['STANDAARD', 'NMBRS'] as const
export type PayrollExportVariant = (typeof PAYROLL_EXPORT_VARIANT)[number]

export const PAYROLL_EXPORT_VARIANT_LABELS: Record<PayrollExportVariant, string> = {
  STANDAARD: 'Standaard (NL-salarispakket)',
  NMBRS:     'NMBRS',
}

export const PAYROLL_EXPORT_SCOPE = ['TOTAAL', 'MUTATIES'] as const
export type PayrollExportScope = (typeof PAYROLL_EXPORT_SCOPE)[number]

export const PAYROLL_EXPORT_SCOPE_LABELS: Record<PayrollExportScope, string> = {
  TOTAAL:   'Totaal (contracturen + mutaties)',
  MUTATIES: 'Alleen mutaties',
}

/** Query op GET /payroll/exports/:id/download — variant + scope (download-tijd). */
export const payrollDownloadQuerySchema = z.object({
  variant: z.enum(PAYROLL_EXPORT_VARIANT).default('STANDAARD'),
  scope:   z.enum(PAYROLL_EXPORT_SCOPE).default('TOTAAL'),
})
export type PayrollDownloadQuery = z.infer<typeof payrollDownloadQuerySchema>

// ─── #51 — Instelbare NMBRS-looncomponent-codes (VOORBEELD, niet gegokt) ──────
//
// De codebase vult bewust GEEN echte NMBRS-looncodes in (zie schema.prisma
// VerlofBeleid-comment). Deze mapping is een DB-singleton met expliciete
// VOORBEELD-defaults; HR/SALARIS vult de echte codes in. `isVoorbeeld` blijft
// true tot iemand de codes bevestigt.

export const NMBRS_LOONCOMPONENT_KEYS = [
  'contractUren',
  'brutoUren',
  'overuren',
  'ploegtoeslag',
  'reistijdUren',
  // batch-54 payroll-km (FO-411) — de oude `reisVergoeding`-code (km als BRUTO
  // looncomponent) is VERVALLEN: kilometers zijn klantfacturatie, geen loon.
  // Wat overblijft is de uitzondering: `kmVergoedingNetto` = de km-vergoeding
  // van een medewerker met de regeling "eigen vervoer", als NETTO-onkosten met
  // een EIGEN looncode. Bewust een nieuwe key i.p.v. hergebruik van de oude:
  // een netto-onkostencode is een andere code dan de oude bruto-code, en die
  // moet met de salarisadministratie (Nmbrs) worden afgestemd vóór gebruik —
  // de export blokkeert liever dan de oude code stil te hergebruiken.
  'kmVergoedingNetto',
  'declaraties',
  'zaterdagToeslag',
  'zondagToeslag',
  'feestdagToeslag',
  'overigeToeslag',
  // Batch-9-vervolg V4 (PO #2/#5) — verlof + ziekte als aparte uren-componenten
  // (naast de bestaande *-uitbetaling-bedragen) + consignatie. Elk een eigen
  // NMBRS-looncomponentcode.
  'verlofUren',
  'verlofUitbetaling',
  'ziekUren',
  'ziekUitbetaling',
  'consignatie',
  'verjaardagToeslag',
] as const
export type NmbrsLooncomponentKey = (typeof NMBRS_LOONCOMPONENT_KEYS)[number]

export const NMBRS_LOONCOMPONENT_LABELS: Record<NmbrsLooncomponentKey, string> = {
  contractUren:      'Contracturen',
  brutoUren:         'Normale uren',
  overuren:          'Overuren',
  ploegtoeslag:      'Ploegentoeslag',
  reistijdUren:      'Reisuren',
  kmVergoedingNetto: 'Km-vergoeding netto (eigen vervoer)',
  declaraties:       'Declaratie',
  zaterdagToeslag:   'Zaterdagtoeslag',
  zondagToeslag:     'Zondagtoeslag',
  feestdagToeslag:   'Feestdagtoeslag',
  overigeToeslag:    'Overige toeslag',
  verlofUren:        'Verlof (uren)',
  verlofUitbetaling: 'Verlof-uitbetaling',
  ziekUren:          'Ziekte (uren)',
  ziekUitbetaling:   'Ziekte-uitbetaling',
  consignatie:       'Consignatie',
  verjaardagToeslag: 'Verjaardagtoeslag',
}

// ─── FO-085 — categorie-context + instelbare exportformaat-config ─────────────
//
// `categorie` groepeert de looncomponenten puur visueel in het beheerscherm
// (uursoort / toeslag / verlofsoort). Geen DB-kolom — een statische presentatie-
// map. De `formaat`-config maakt de Nmbrs-export-vorm (decimaalteken + kolom-set/
// -volgorde/-namen) instelbaar zodat het bestand exact het door Nmbrs
// voorgeschreven importformaat volgt zodra dat bekend is.

export const NMBRS_LOONCOMPONENT_CATEGORIE = [
  'UURSOORT',
  'TOESLAG',
  'VERLOFSOORT',
] as const
export type NmbrsLooncomponentCategorie = (typeof NMBRS_LOONCOMPONENT_CATEGORIE)[number]

export const NMBRS_LOONCOMPONENT_CATEGORIE_LABELS: Record<NmbrsLooncomponentCategorie, string> = {
  UURSOORT:    'Uursoorten',
  TOESLAG:     'Toeslagen & vergoedingen',
  VERLOFSOORT: 'Verlof & ziekte',
}

/** Bron-categorie per looncomponent (presentatie/groepering, geen DB-kolom). */
export const NMBRS_LOONCOMPONENT_CATEGORIE_MAP: Record<NmbrsLooncomponentKey, NmbrsLooncomponentCategorie> = {
  contractUren:      'UURSOORT',
  brutoUren:         'UURSOORT',
  overuren:          'UURSOORT',
  reistijdUren:      'UURSOORT',
  ploegtoeslag:      'TOESLAG',
  kmVergoedingNetto: 'TOESLAG',
  declaraties:       'TOESLAG',
  zaterdagToeslag:   'TOESLAG',
  zondagToeslag:     'TOESLAG',
  feestdagToeslag:   'TOESLAG',
  overigeToeslag:    'TOESLAG',
  consignatie:       'TOESLAG',
  verjaardagToeslag: 'TOESLAG',
  verlofUren:        'VERLOFSOORT',
  verlofUitbetaling: 'VERLOFSOORT',
  ziekUren:          'VERLOFSOORT',
  ziekUitbetaling:   'VERLOFSOORT',
}

/**
 * Beschikbare kolom-id's voor de Nmbrs-export (long-format). De `waarde`-kolom is
 * de huidige enkele-waarde-kolom; `aantal`/`bedrag` zijn voorbereid voor een
 * Nmbrs-format dat uren en bedragen in aparte kolommen wil (aantal vult alleen
 * voor uren-componenten, bedrag alleen voor bedrag-componenten).
 */
export const NMBRS_KOLOM_IDS = [
  'werknemernummer',
  'looncomponentcode',
  'waarde',
  'aantal',
  'bedrag',
] as const
export type NmbrsKolomId = (typeof NMBRS_KOLOM_IDS)[number]

export const NMBRS_KOLOM_LABELS: Record<NmbrsKolomId, string> = {
  werknemernummer:   'Medewerkernummer',
  looncomponentcode: 'Looncomponent',
  waarde:            'Waarde',
  aantal:            'Aantal',
  bedrag:            'Bedrag',
}

/** Instelbare exportformaat-config (FO-085). */
export interface NmbrsFormaatDTO {
  /** Decimaalteken voor uren/bedragen (komma = NL-pakket, punt = sommige imports). */
  decimaalteken: ',' | '.'
  /** Geordende kolom-id's; bepaalt de kolommen én hun volgorde in de export. */
  kolommen: NmbrsKolomId[]
  /** Optionele header-override per kolom (leeg = het standaard-label). */
  kolomNamen: Partial<Record<NmbrsKolomId, string>>
}

/**
 * VEILIGE DEFAULT (FO-085): de huidige 3-koloms long-format met komma-decimaal.
 * Blijft gelden tot de opdrachtgever het exacte Nmbrs-importformaat aanlevert;
 * dan wordt alleen deze kolomdefinitie aangepast (config-data, geen code-wijziging).
 */
export const NMBRS_FORMAAT_DEFAULT: NmbrsFormaatDTO = {
  decimaalteken: ',',
  kolommen: ['werknemernummer', 'looncomponentcode', 'waarde'],
  kolomNamen: {},
}

export interface NmbrsInstellingDTO {
  /** Per looncomponent de NMBRS-code; lege string = nog niet ingevuld. */
  codes: Record<NmbrsLooncomponentKey, string>
  scheidingsteken: string
  headersOpnemen: boolean
  /** FO-085 — exportformaat-config (altijd gevuld; gemerged met de defaults). */
  formaat: NmbrsFormaatDTO
  /** True zolang de VOORBEELD-codes niet bevestigd zijn (UI-waarschuwing). */
  isVoorbeeld: boolean
  /**
   * FO-389 (batch-46) — de consignatie-looncomponent is geactiveerd: SALARIS
   * heeft de echte U-code in `codes.consignatie` gezet én de activatie bevestigd.
   * Pas dan telt/exporteert de component (§3.5). Activeren met een VB--code is
   * server-side geweigerd.
   */
  consignatieActief: boolean
  /**
   * FO-389 — verloningseenheid. In v1 altijd `WEEK` (per piketweek); niet
   * instelbaar. `NACHT` is gereserveerd voor de v2 per-nacht-variant.
   */
  consignatieEenheid: ConsignatieEenheid
  updatedAt: string | null
}

/** V4 (PO #5) — één audit-regel van een NMBRS-mapping-wijziging. */
export interface NmbrsAuditDTO {
  id: string
  actorId: string
  actorNaam: string | null
  actie: string
  createdAt: string
}

const nmbrsCodeRecordSchema = z.record(
  z.enum(NMBRS_LOONCOMPONENT_KEYS),
  z.string().trim().max(40),
)

/** FO-085 — exportformaat-config (gedeeld door API en beheerscherm). */
export const nmbrsFormaatSchema = z.object({
  decimaalteken: z.enum([',', '.']),
  kolommen:      z.array(z.enum(NMBRS_KOLOM_IDS)).min(1).max(NMBRS_KOLOM_IDS.length),
  kolomNamen:    z.record(z.enum(NMBRS_KOLOM_IDS), z.string().trim().max(60)).optional(),
})

export const updateNmbrsInstellingSchema = z.object({
  codes:           nmbrsCodeRecordSchema.optional(),
  scheidingsteken: z.enum([';', ',', '\t']).optional(),
  headersOpnemen:  z.boolean().optional(),
  formaat:         nmbrsFormaatSchema.optional(),
  isVoorbeeld:     z.boolean().optional(),
  // FO-389 (batch-46) — consignatie-activatie. `consignatieActief=true` is
  // server-side geweigerd zolang `codes.consignatie` leeg/een VB--code is.
  // De eenheid is in v1 NIET instelbaar (blijft WEEK): per-nacht (`STANDBY_NACHT`)
  // is een v2-variant die de werkelijke nachten telt, niet `weken × nacht-tarief`.
  consignatieActief:  z.boolean().optional(),
})
export type UpdateNmbrsInstellingInput = z.infer<typeof updateNmbrsInstellingSchema>

// ─── #52 — Verloningsperiode-overzicht (mutaties vorige maand + uren huidige) ─
//
// Read-only naast-elkaar-weergave die de NMBRS-koppeling expliciet maakt: NMBRS
// pakt de contracturen van de HUIDIGE maand, STREEP levert de MUTATIES van de
// VORIGE maand. Beide kolommen komen uit `aggregatePayrollMonth` (geen nieuwe
// rekenlaag).

/**
 * FO-149 (batch-29) — bron van de contracturen in payroll-rijen.
 *
 * - `MUTATIE`: op de payroll-peildatum is een actieve `EmployeeContractMutation`
 *   gevonden; de contracturen (per week én periode-norm) komen uit die mutatie.
 *   Dit is de bedoelde bron van waarheid.
 * - `CACHE`: er is GEEN actieve mutatie op de peildatum (lege of te laat
 *   beginnende tijdlijn); de contracturen vallen expliciet terug op de
 *   Employee-cachevelden in plaats van stil 0. De UI toont hierbij een
 *   "tijdlijn aanvullen"-signaal richting HR.
 */
export const CONTRACT_UREN_BRONNEN = ['MUTATIE', 'CACHE'] as const
export type ContractUrenBron = (typeof CONTRACT_UREN_BRONNEN)[number]

/**
 * FO-305 P4 (batch-33) — overuren per CAO-trede. De som van de vier treden is
 * exact het oude (week-net) overuren-totaal (`p100 + p125 + p150 + p200`).
 * `p100` = Meeruren 100% (boven contracturen, geen premie).
 */
export interface OverurenTredenDTO {
  p100: number
  p125: number
  p150: number
  p200: number
}

/**
 * FO-305 P6 (batch-33) — toeslag-uren per ploegsoort + dagtype. De som van alle
 * velden is exact de totale toeslag-uren-basis (elke toeslag-component valt in
 * precies één veld). `zaterdag`/`zondag` zijn UITSLUITEND incidenteel weekendwerk
 * BUITEN het 5-ploegenrooster; za/zo binnen een 5-ploegendienst telt in
 * `vijfPloegen`. `feestdag` + `overig` (4-ploegen, oud-model, restdagtype) worden
 * in v1 in het detail/tooltip getoond, niet als eigen kolom.
 */
export interface ToeslagUrenDTO {
  tweePloegen: number
  driePloegen: number
  vijfPloegen: number
  zaterdag: number
  zondag: number
  feestdag: number
  overig: number
}

/**
 * FO-318 (batch-35) §3.1 — het gebruikte ploegensysteem per medewerker mét bron,
 * zodat het verloningsperiode-scherm de casus-Ojah controleerbaar maakt.
 * `bron`: KLANTROOSTER (autoritatief) / WERKBON (create-time fallback) / GEEN.
 * `conflict`: klantrooster en werkbon-dienstType gaven een verschillend systeem
 * (bv. rooster 2-ploegen, werkbon 3-ploegen) — het scherm markeert dit.
 */
export interface PloegsoortBronDTO {
  /** Ploegensysteem-code (`PLOEGEN_2` etc.) of `DAGDIENST`/`CONSIGNATIE`. */
  dienstType: string
  /** Leesbaar label ("2-ploegen"). */
  label: string
  bron: 'KLANTROOSTER' | 'WERKBON' | 'GEEN'
  /**
   * FO-344 (batch-40) §3.1 — verbijzondering van bron KLANTROOSTER: volgt de
   * classificatie het operationele rooster (`ROOSTER`) of geldt er een
   * afwijkende toeslagclassificatie (`OVERRIDE`, casus Ojah)? `null` bij de
   * bronnen WERKBON/GEEN. Het scherm toont bij OVERRIDE het label
   * "Klantrooster (afwijkende classificatie)".
   */
  bronDetail: 'ROOSTER' | 'OVERRIDE' | null
  /**
   * FO-344 — het OPERATIONELE ploegensysteem van het rooster wanneer dat afwijkt
   * van `dienstType` (alleen gevuld bij `bronDetail: 'OVERRIDE'`). Voedt de
   * tooltip: "Operationeel 3-ploegen, verloond en gefactureerd als 2-ploegen."
   */
  operationeelDienstType: string | null
  conflict: boolean
  /** Toeslag-uren die op dit ploegensysteem zijn geboekt (0 = alleen als bron-info). */
  uren: number
}

export interface VerloningsperiodeOverzichtRowDTO {
  employeeId: string
  employeeNumber: string
  naam: string
  /** payroll-werkruimte 18→8 — BV van de medewerker, voor het BV-filter op het scherm. */
  entityId: string | null
  // Huidige maand — de uren (waar NMBRS de contracturen bij pakt).
  huidigeUren: number
  huidigeOveruren: number
  huidigeReisUren: number
  /**
   * FO-007 — reisuren-verrekening tegen de contracturen (zichtbaarheid, geen
   * wijziging van het uit te betalen bedrag). `huidigeContractUren` is de
   * werkbare contracturen-norm voor de periode (dezelfde bron als de payroll-
   * capaciteits-snapshot); 0 betekent geen contracturen op peildatum (oproep/
   * nul-uren) → niets te verrekenen. `huidigeVerrekendeReisUren` zijn de
   * reisuren die het tekort tot de contracturen aanvullen,
   * `huidigeOverigeReisUren` de rest. Per definitie geldt:
   * huidigeVerrekendeReisUren + huidigeOverigeReisUren === huidigeReisUren.
   */
  huidigeContractUren: number
  /**
   * FO-149 (batch-29) — contracturen per week uit dezelfde bron als
   * `huidigeContractUren` (de op-peildatum-actieve contractmutatie, met
   * expliciete cache-fallback). `null` wanneer de medewerker geen rij in de
   * huidige maand heeft (alleen vorige-maand-mutaties). Samen met
   * `huidigeContractUrenBron` maakt dit het onderscheid tussen "echt 0 uur"
   * (oproep-/nul-urencontract: bron MUTATIE, 0 per week) en "geen
   * contractgegevens" (bron CACHE: tijdlijn aanvullen) zichtbaar.
   */
  huidigeContractUrenPerWeek: number | null
  huidigeContractUrenBron: ContractUrenBron | null
  huidigeVerrekendeReisUren: number
  huidigeOverigeReisUren: number
  /**
   * FO-318 §3.1 — het/de gebruikte ploegensysteem/-en van de HUIDIGE maand mét
   * bron (KLANTROOSTER autoritatief → WERKBON fallback → GEEN) + conflict-vlag,
   * zodat de casus-Ojah op het scherm controleerbaar is.
   */
  huidigePloegsoortBronnen: PloegsoortBronDTO[]
  // Vorige maand — de mutaties die STREEP doorgeeft.
  vorigeOveruren: number
  /**
   * FO-305 P4 (batch-33) — de vorige-maand-overuren uitgesplitst per CAO-trede.
   * `p100 + p125 + p150 + p200 === vorigeOveruren` (som = de oude kolom).
   */
  vorigeOverurenTreden: OverurenTredenDTO
  vorigeToeslagBedrag: number
  /**
   * FO-305 P6 (batch-33) — de vorige-maand-toeslag als UREN per ploegsoort +
   * za/zo (+ feestdag/overig in detail), i.p.v. het geldbedrag. Het bedrag
   * (`vorigeToeslagBedrag`) blijft in de DTO voor de doorklik naar de reservering.
   */
  vorigeToeslagUren: ToeslagUrenDTO
  vorigeReisVergoeding: number
  /**
   * FO-305 P5 (batch-33) — uit te betalen reisuren van de mutatie-maand (N-1):
   * de reisuren die na de contracturen-verrekening overblijven
   * (`resterendeReisUren`). Vervangt de reis-geldkolom op dit overzicht; het
   * bedrag is via de reservering bereikbaar.
   */
  vorigeReisUren: number
  vorigeDeclaraties: number
  vorigeVerlofUitbetaling: number
  vorigeBrutoTotaal: number
}

export interface VerloningsperiodeOverzichtDTO {
  huidigeJaar: number
  huidigeMaand: number
  vorigeJaar: number
  vorigeMaand: number
  rows: VerloningsperiodeOverzichtRowDTO[]
}

export const verloningsperiodeOverzichtQuerySchema = z.object({
  year:  yearSchema,
  month: monthSchema,
})
export type VerloningsperiodeOverzichtQuery = z.infer<
  typeof verloningsperiodeOverzichtQuerySchema
>

// ─── V5 (PO #10) — generiek periode-overzicht (vrij/week/maand/kwartaal/jaar) ─
// Bekijk-laag bovenop de bestaande uren-aggregatie: per medewerker de uren over
// de gekozen periode. De export loopt via de free-range werkuren-export.

export const PAYROLL_PERIODE_TYPE_VALUES = ['VRIJ', 'WEEK', 'MAAND', 'KWARTAAL', 'JAAR'] as const

export const payrollPeriodeOverzichtQuerySchema = z.object({
  type:     z.enum(PAYROLL_PERIODE_TYPE_VALUES),
  jaar:     yearSchema,
  maand:    monthSchema.optional(),
  week:     z.coerce.number().int().min(1).max(53).optional(),
  kwartaal: z.coerce.number().int().min(1).max(4).optional(),
  van:      z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  totEnMet: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
})
export type PayrollPeriodeOverzichtQuery = z.infer<typeof payrollPeriodeOverzichtQuerySchema>

export interface PayrollPeriodeOverzichtRowDTO {
  employeeId: string
  employeeNumber: string
  naam: string
  werkdagen: number
  uren: number
  overuren: number
  reisUren: number
}

export interface PayrollPeriodeOverzichtDTO {
  /** De gekozen periode, omgezet naar een concrete range + label. */
  van: string
  totEnMet: string
  label: string
  rows: PayrollPeriodeOverzichtRowDTO[]
}
